import { getInitials } from "@repo/shared";
import { FileText, PanelLeftOpen } from "lucide-react";
import { useRef, useState } from "react";
import { ClaudiaIcon } from "@/components/claudia-icon";
import { ShieldCogIcon } from "@/components/shield-cog-icon";
import { useSidebar } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UnreadDot } from "@/components/ui/unread-dot";
import { useOrganization } from "@/contexts/organization-context";
import { useTabs, useTabsState } from "@/contexts/tabs-context";
import { ConversationsFlyout } from "@/features/project/components/conversations/conversations-flyout";
import { useConversationsFlyout } from "@/features/project/hooks/use-conversations-flyout";
import { useProjectBySlug } from "@/features/project/hooks/useProjectData";
import { resolveFrameworkIcon } from "@/features/project/lib/icon-registry";
import {
  getCollapsedRailItems,
  isProjectLevelNavItemActive,
  PROJECT_LEVEL_CLAUDIA_NODE_ID,
} from "@/features/project/lib/project-level-nav-items";
import { useAuthUserDisplay } from "@/hooks/use-auth-user-display";
import { useFeatureFlags } from "@/hooks/use-feature-flags";

/**
 * CollapsedSidebar - Contextual icon strip shown when the left sidebar is collapsed.
 *
 * Replaces SidebarExpandEdge with a richer 56px sidebar that shows:
 * - Home view: org icon, expand button, profile avatar
 * - Project view: project icon, expand, AI chat, nav links, entity icon, profile
 *
 * Desktop only. On mobile, the sidebar uses Sheet drawers instead.
 */
export function CollapsedSidebar({ isProjectRoute }: { isProjectRoute: boolean }) {
  const { open, isMobile } = useSidebar();
  const { activeWorkspaceKind } = useTabsState();

  if (open || isMobile) return null;

  if (isProjectRoute) {
    return <CollapsedProjectSidebar />;
  }

  if (activeWorkspaceKind === "admin") {
    return <CollapsedAdminSidebar />;
  }

  return <CollapsedHomeSidebar />;
}

// ─── Shared pieces ───────────────────────────────────────────────────────────

function Divider() {
  return <div className="h-px w-8 shrink-0 bg-border" />;
}

function CollapsedSidebarAvatarButton({
  initials,
  onClick,
  avatarClassName,
  initialsClassName,
  testId,
  avatarTestId,
}: {
  initials: string;
  onClick: () => void;
  avatarClassName: string;
  initialsClassName: string;
  testId?: string;
  avatarTestId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex size-8 shrink-0 select-none items-center justify-center transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="Expand sidebar"
    >
      <span
        data-testid={avatarTestId}
        className={`flex size-7 items-center justify-center select-none ${avatarClassName}`}
      >
        <span className={`select-none ${initialsClassName}`}>{initials}</span>
      </span>
    </button>
  );
}

function ProfileAvatar({ onClick }: { onClick: () => void }) {
  const { initials } = useAuthUserDisplay();

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-8 shrink-0 items-center justify-center bg-neutral-muted"
      aria-label="Expand sidebar"
    >
      <span className="text-[11px] font-semibold text-muted-foreground">{initials}</span>
    </button>
  );
}

function ExpandButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger
        onClick={onClick}
        className="flex size-9 shrink-0 items-center justify-center text-neutral-dim hover:text-foreground transition-colors"
        aria-label="Expand sidebar"
      >
        <PanelLeftOpen className="size-[18px]" />
      </TooltipTrigger>
      <TooltipContent side="right">Expand sidebar</TooltipContent>
    </Tooltip>
  );
}

interface NavButtonProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  isActive?: boolean;
}

function NavButton({ icon: Icon, label, onClick, isActive = false }: NavButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        onClick={onClick}
        className={`flex size-9 shrink-0 items-center justify-center transition-colors ${
          isActive ? "bg-muted text-primary" : "text-neutral-dim hover:text-foreground"
        }`}
        aria-label={label}
      >
        <Icon className="size-4" />
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The collapsed rail's Claudia icon.
 *
 * It opens the same conversations flyout the expanded nav row does, anchored to the icon
 * itself, rather than navigating to a single remembered assistant path.
 *
 * The rail sits outside `ProjectRouteProvider`, which is why the flyout takes its navigation as
 * a prop rather than reaching for the route context.
 */
function CollapsedClaudiaButton({
  isActive,
  currentPath,
  projectSlug,
  onNavigate,
}: {
  isActive: boolean;
  currentPath: string;
  projectSlug: string | null;
  onNavigate: (path: string) => void;
}) {
  const { organizationId } = useOrganization();
  const project = useProjectBySlug(organizationId, projectSlug);
  // The same hook the expanded rail's nav row uses, so the two switchers cannot drift. This rail
  // only renders while it *is* the visible one, so it takes the hook's default `isActive`.
  const conversations = useConversationsFlyout({ projectId: project?._id ?? null, onNavigate });
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <>
      {/* The label tooltip and the flyout both open to the right of a 36px icon, so the tooltip
          is held closed while the flyout is up rather than drawn on top of it. */}
      <Tooltip open={isTooltipOpen && !conversations.isOpen} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger
          ref={buttonRef}
          onClick={conversations.activate}
          aria-label="Claudia"
          // A project with no conversation goes straight to a new chat (LAT-1077), and a button
          // that navigates does not announce a popup it will not open.
          aria-haspopup={conversations.opensFlyout ? "dialog" : undefined}
          aria-expanded={conversations.opensFlyout ? conversations.isOpen : undefined}
          className={`relative flex size-9 shrink-0 items-center justify-center transition-colors ${
            isActive || conversations.isOpen
              ? "bg-muted text-primary"
              : "text-neutral-dim hover:text-foreground"
          }`}
        >
          <ClaudiaIcon className="size-4" />
          {conversations.needsYouCount > 0 ? (
            <span
              data-testid="claudia-rail-needs-you-dot"
              className="absolute right-1.5 top-1.5 flex items-center"
            >
              <UnreadDot active />
            </span>
          ) : null}
        </TooltipTrigger>
        <TooltipContent side="right">Claudia</TooltipContent>
      </Tooltip>
      <ConversationsFlyout
        open={conversations.isOpen}
        onOpenChange={conversations.setIsOpen}
        anchorRef={buttonRef}
        items={conversations.items}
        isLoading={conversations.isLoading}
        currentPath={currentPath}
        onNavigate={onNavigate}
      />
    </>
  );
}

// ─── Home variant ────────────────────────────────────────────────────────────

function CollapsedHomeSidebar() {
  const { toggleSidebar } = useSidebar();
  const { organization } = useOrganization();

  const orgName = organization?.name ?? "Organization";
  const orgInitial = orgName[0]?.toUpperCase() ?? "O";

  return (
    <div className="hidden md:flex w-14 shrink-0 flex-col items-center gap-2 bg-sidebar border-r border-border py-3 pb-4">
      {/* Org icon */}
      <CollapsedSidebarAvatarButton
        testId="collapsed-home-sidebar-org-trigger"
        avatarTestId="collapsed-home-sidebar-org-avatar"
        initials={orgInitial}
        onClick={toggleSidebar}
        avatarClassName="bg-primary"
        initialsClassName="text-[13px] font-bold text-primary-foreground"
      />

      <Divider />
      <ExpandButton onClick={toggleSidebar} />

      {/* Spacer */}
      <div className="flex-1" />

      <ProfileAvatar onClick={toggleSidebar} />
    </div>
  );
}

// ─── Project variant ─────────────────────────────────────────────────────────

function CollapsedProjectSidebar() {
  const { toggleSidebar } = useSidebar();
  const { flags: featureFlags } = useFeatureFlags();
  const { tabs, activeTabId } = useTabsState();
  const { navigateTab } = useTabs();

  const activeTab = activeTabId ? tabs.find((t) => t.tabId === activeTabId) : null;
  const projectSlug = activeTab?.kind === "project" ? activeTab.resourceId : null;
  const projectName = activeTab?.name ?? "";
  const projectInitials = getInitials(projectName);
  const currentPath = activeTab?.currentPath ?? "/";

  const navigateTo = (path: string) => {
    if (activeTabId) {
      navigateTab(activeTabId, path);
    }
  };

  const entityIconKey = activeTab?.memory.entityIconKey ?? null;
  const entityPath = activeTab?.memory.entityPath ?? null;
  const EntityIcon = entityIconKey ? resolveFrameworkIcon(entityIconKey, FileText) : null;
  const isLastEntityActive = entityPath !== null && currentPath === entityPath;

  return (
    <div
      data-testid="collapsed-project-sidebar"
      className="hidden md:flex w-14 shrink-0 flex-col items-center gap-1 bg-sidebar border-r border-border py-3 pb-4"
    >
      {/* Project initials */}
      <CollapsedSidebarAvatarButton
        testId="collapsed-project-sidebar-title-trigger"
        avatarTestId="collapsed-project-sidebar-title-avatar"
        initials={projectInitials}
        onClick={toggleSidebar}
        avatarClassName="bg-primary"
        initialsClassName="text-[11px] font-semibold text-primary-foreground"
      />

      <Divider />

      <ExpandButton onClick={toggleSidebar} />

      <Divider />

      {/* Project-level page navigation, in `PROJECT_LEVEL_NAV_ITEMS` order. Claudia is the one
          row with its own renderer: it opens the conversations flyout instead of navigating.
          `display: contents` keeps the icons direct children of the rail's own flex column, so
          the group is nameable without changing the layout. */}
      <div data-testid="collapsed-project-level-nav" className="contents">
        {getCollapsedRailItems(featureFlags).map((item) =>
          item.id === PROJECT_LEVEL_CLAUDIA_NODE_ID ? (
            <CollapsedClaudiaButton
              key={item.id}
              isActive={isProjectLevelNavItemActive(item, currentPath)}
              currentPath={currentPath}
              projectSlug={projectSlug}
              onNavigate={navigateTo}
            />
          ) : (
            <NavButton
              key={item.id}
              icon={item.icon}
              label={item.label}
              isActive={isProjectLevelNavItemActive(item, currentPath)}
              onClick={() => navigateTo(item.path)}
            />
          )
        )}
      </div>

      <Divider />

      {/* Currently selected entity icon */}
      {EntityIcon && entityPath && (
        <Tooltip>
          <TooltipTrigger
            onClick={() => navigateTo(entityPath)}
            className={`flex size-9 shrink-0 items-center justify-center transition-colors ${
              isLastEntityActive
                ? "bg-muted text-primary"
                : "text-neutral-dim hover:text-foreground"
            }`}
            aria-label="Go to last entity"
          >
            <EntityIcon className="size-4" />
          </TooltipTrigger>
          <TooltipContent side="right">Last element</TooltipContent>
        </Tooltip>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      <ProfileAvatar onClick={toggleSidebar} />
    </div>
  );
}

function CollapsedAdminSidebar() {
  const { toggleSidebar } = useSidebar();
  const { activeTabId } = useTabsState();
  const { navigateTab } = useTabs();

  return (
    <div className="hidden md:flex w-14 shrink-0 flex-col items-center gap-1 border-r border-border bg-sidebar py-3 pb-4">
      <CollapsedSidebarAvatarButton
        initials="AP"
        onClick={toggleSidebar}
        avatarClassName="bg-primary"
        initialsClassName="text-[11px] font-semibold text-primary-foreground"
      />

      <Divider />
      <ExpandButton onClick={toggleSidebar} />
      <NavButton
        icon={ShieldCogIcon}
        label="Admin Portal"
        isActive
        onClick={() => {
          if (activeTabId) {
            navigateTab(activeTabId, "/playground");
          }
        }}
      />

      <div className="flex-1" />

      <ProfileAvatar onClick={toggleSidebar} />
    </div>
  );
}
